import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  X,
  Loader2,
  Settings,
  Link,
  FileText,
  RefreshCw,
  AlertTriangle,
  Keyboard,
  ListTree,
} from "lucide-react";
import Input from "../../widgets/Input";
import CustomDropdownSelect from "../../widgets/CustomDropdownSelect";
import {
  DatabaseConfig,
  CreateDatabaseConfigData,
  UpdateDatabaseConfigData,
  DATABASE_PROVIDERS,
  DbInspectItem,
  LARGE_DATASET_BYTES,
  databaseConfigService,
} from "../../services/databaseConfig.service";

interface DatabaseConfigFormProps {
  isOpen: boolean;
  onClose: () => void;
  config: DatabaseConfig | null;
  onSubmit: (
    data: CreateDatabaseConfigData | UpdateDatabaseConfigData
  ) => Promise<void>;
  isLoading: boolean;
}

// Human-readable byte size, e.g. 2.3 GB / 880 MB / 14 KB.
function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const val = bytes / Math.pow(1024, i);
  return `${val >= 10 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
}

// Compact row counts, e.g. 1.2k / 3.4M.
function formatCount(n?: number): string {
  if (n == null) return "";
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

const isLarge = (b?: number) => (b ?? 0) >= LARGE_DATASET_BYTES;

function errMessage(e: any): string {
  return (
    e?.response?.data?.error?.error_description ||
    e?.response?.data?.error_description ||
    e?.response?.data?.message ||
    e?.message ||
    "Could not connect to the database."
  );
}

export default function DatabaseConfigForm({
  isOpen,
  onClose,
  config,
  onSubmit,
  isLoading,
}: DatabaseConfigFormProps) {
  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CreateDatabaseConfigData>({ mode: "onChange" });

  const isEdit = !!config;
  const provider = watch("database_provider");
  const isPostgres = provider === "postgres";
  const ingestMode = watch("ingest_mode");
  const isDataMode = ingestMode === "DATA";

  const connectionString = watch("connection_string");
  const databaseName = watch("database_name");
  const collectionName = watch("collection_name");

  // Introspection state for the cascading dropdowns.
  const [dbItems, setDbItems] = useState<DbInspectItem[]>([]);
  const [collItems, setCollItems] = useState<DbInspectItem[]>([]);
  const [loadingDbs, setLoadingDbs] = useState(false);
  const [loadingColls, setLoadingColls] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [collError, setCollError] = useState<string | null>(null);
  // Fallback to plain text inputs (when introspection isn't possible / not wanted).
  const [manualEntry, setManualEntry] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset transient introspection state on every open.
      setDbItems([]);
      setCollItems([]);
      setDbError(null);
      setCollError(null);
      setManualEntry(false);

      if (config) {
        reset({
          config_name: config.config_name,
          database_provider: config.database_provider,
          connection_string: config.connection_string,
          database_name: config.database_name,
          collection_name: config.collection_name,
          ingest_mode: config.ingest_mode || "FILES",
          data_granularity: config.data_granularity || "PER_ITEM",
          data_format: config.data_format || "JSON",
          filename_field: config.filename_field || "",
          data_file_field: config.data_file_field || "",
        });
      } else {
        reset({
          config_name: "",
          database_provider: "mongodb",
          connection_string: "",
          database_name: "",
          collection_name: "",
          ingest_mode: "FILES",
          data_granularity: "PER_ITEM",
          data_format: "JSON",
          filename_field: "",
          data_file_field: "",
        });
      }
    }
  }, [isOpen, config, reset]);

  const itemOption = (d: DbInspectItem) => ({
    value: d.name,
    label: d.name,
    size: formatBytes(d.size_bytes),
    rows: d.count != null ? `${formatCount(d.count)} rows` : undefined,
    warn: isLarge(d.size_bytes),
  });

  // Build dropdown options; always include the current value so edits render
  // before a fresh introspection has run.
  const dbOptions = useMemo(() => {
    const opts = dbItems.map(itemOption);
    if (databaseName && !opts.some((o) => o.value === databaseName)) {
      opts.unshift({ value: databaseName, label: databaseName, size: undefined, rows: undefined, warn: false });
    }
    return opts;
  }, [dbItems, databaseName]);

  const collOptions = useMemo(() => {
    const opts = collItems.map(itemOption);
    if (collectionName && !opts.some((o) => o.value === collectionName)) {
      opts.unshift({ value: collectionName, label: collectionName, size: undefined, rows: undefined, warn: false });
    }
    return opts;
  }, [collItems, collectionName]);

  const selectedDb = dbItems.find((d) => d.name === databaseName);
  const selectedColl = collItems.find((d) => d.name === collectionName);

  const loadDatabases = async () => {
    const cs = (connectionString || "").trim();
    if (!cs) {
      setDbError("Enter a connection string first.");
      return;
    }
    setLoadingDbs(true);
    setDbError(null);
    setCollError(null);
    try {
      const items = await databaseConfigService.inspectDatabases(provider, cs);
      setDbItems(items);
      if (items.length === 0) setDbError("No databases found on this connection.");
    } catch (e) {
      // Keep the dropdown visible so the error shows and Connect can be retried;
      // the user can switch to manual entry themselves via the toggle.
      setDbError(errMessage(e));
    } finally {
      setLoadingDbs(false);
    }
  };

  const loadCollections = async (dbName?: string) => {
    const cs = (connectionString || "").trim();
    if (!cs) return;
    const name = dbName ?? databaseName;
    if (!isPostgres && !name) return; // Mongo needs a database to scope collections
    setLoadingColls(true);
    setCollError(null);
    try {
      const items = await databaseConfigService.inspectCollections(provider, cs, name);
      setCollItems(items);
      if (items.length === 0)
        setCollError(`No ${isPostgres ? "tables" : "collections"} found.`);
    } catch (e) {
      setCollError(errMessage(e));
    } finally {
      setLoadingColls(false);
    }
  };

  const handleFormSubmit = (data: CreateDatabaseConfigData) => {
    onSubmit(data);
  };

  if (!isOpen) return null;

  const collLabel = isPostgres ? "Table" : "Collection";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit
              ? "Edit Database Configuration"
              : "Create New Database Configuration"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="p-6 space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Basic Information
              </h3>
            </div>

            <Input
              name="config_name"
              control={control}
              label="Configuration Name"
              placeholder="e.g., My Database Config"
              rules={{
                required: "Configuration name is required",
                pattern: {
                  value: /^[A-Za-z0-9\s_-]*$/,
                  message:
                    "Only alphabets, numbers, spaces, hyphens, and underscores are allowed",
                },
              }}
            />

            <CustomDropdownSelect
              name="database_provider"
              control={control as any}
              label="Database Provider"
              placeholder="Select database provider"
              options={DATABASE_PROVIDERS.map((p) => ({
                value: p.value,
                label: p.label,
              }))}
              rules={{ required: "Database provider is required" }}
              onSelectionChange={() => {
                // Provider switched — previously fetched lists AND the chosen
                // db/table no longer apply (a Mongo collection name is invalid
                // for Postgres and vice-versa).
                setDbItems([]);
                setCollItems([]);
                setDbError(null);
                setCollError(null);
                setValue("database_name", "");
                setValue("collection_name", "");
              }}
            />
          </div>

          {/* Connection Details */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Connection Details
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setManualEntry((m) => !m);
                  setDbError(null);
                  setCollError(null);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {manualEntry ? (
                  <>
                    <ListTree className="w-3.5 h-3.5" /> Pick from database
                  </>
                ) : (
                  <>
                    <Keyboard className="w-3.5 h-3.5" /> Enter manually
                  </>
                )}
              </button>
            </div>

            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  name="connection_string"
                  control={control}
                  label="Connection String"
                  placeholder={
                    isPostgres
                      ? "postgresql://user:pass@host:25060/dbname?sslmode=require"
                      : "mongodb://localhost:27017"
                  }
                  rules={{
                    required: "Connection string is required",
                    pattern: {
                      value: /^[^<>]*$/,
                      message:
                        "Connection string cannot contain < or > characters",
                    },
                  }}
                />
              </div>
              {!manualEntry && (
                <div className="shrink-0">
                  {/* Invisible spacer label so the button lines up with the input,
                      not its "Connection String" label. */}
                  <label className="block text-sm font-medium mb-2 select-none" aria-hidden="true">
                    &nbsp;
                  </label>
                  <button
                    type="button"
                    onClick={loadDatabases}
                    disabled={loadingDbs || !((connectionString || "").trim())}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Connect and list databases"
                  >
                    {loadingDbs ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Connect
                  </button>
                </div>
              )}
            </div>

            {manualEntry ? (
              // ── Manual fallback (plain text inputs) ──
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="database_name"
                  control={control}
                  label={isPostgres ? "Database Name (optional)" : "Database Name"}
                  placeholder={
                    isPostgres ? "in the connection string" : "e.g., skillzen-dev"
                  }
                  rules={{
                    ...(isPostgres ? {} : { required: "Database name is required" }),
                    pattern: {
                      value: /^[A-Za-z0-9\s_-]*$/,
                      message:
                        "Only alphabets, numbers, spaces, hyphens and underscores are allowed",
                    },
                  }}
                />
                <Input
                  name="collection_name"
                  control={control}
                  label={isPostgres ? "Table Name" : "Collection Name"}
                  placeholder={
                    isPostgres
                      ? "e.g., documents or public.documents"
                      : "e.g., trending-topics"
                  }
                  rules={{
                    required: isPostgres
                      ? "Table name is required"
                      : "Collection name is required",
                    pattern: {
                      value: isPostgres
                        ? /^[A-Za-z0-9_.]*$/
                        : /^[A-Za-z0-9\s_-]*$/,
                      message: isPostgres
                        ? "Use letters, numbers, underscore and optional schema.table"
                        : "Only alphabets, numbers, spaces, hyphens and underscores are allowed",
                    },
                  }}
                />
              </div>
            ) : (
              // ── Cascading, size-aware dropdowns ──
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <CustomDropdownSelect
                    name="database_name"
                    control={control as any}
                    label={isPostgres ? "Database (optional)" : "Database"}
                    placeholder={
                      dbItems.length
                        ? "Select a database"
                        : "Click Connect to load"
                    }
                    options={dbOptions}
                    isLoading={loadingDbs}
                    allowClear
                    rules={
                      isPostgres ? {} : { required: "Database name is required" }
                    }
                    onSelectionChange={(opt) => {
                      const val = (opt && !Array.isArray(opt) && opt.value) || "";
                      // Selecting a new database invalidates the collection list.
                      setValue("collection_name", "");
                      setCollItems([]);
                      setCollError(null);
                      if (val) loadCollections(val);
                    }}
                  />
                  {dbError && (
                    <p className="mt-1 text-xs text-red-500">{dbError}</p>
                  )}
                </div>

                <div>
                  <CustomDropdownSelect
                    name="collection_name"
                    control={control as any}
                    label={collLabel}
                    placeholder={
                      collItems.length
                        ? `Select a ${collLabel.toLowerCase()}`
                        : isPostgres
                        ? "Pick a database or load tables"
                        : "Select a database first"
                    }
                    options={collOptions}
                    isLoading={loadingColls}
                    disable={!isPostgres && !databaseName}
                    allowClear
                    rules={{
                      required: isPostgres
                        ? "Table name is required"
                        : "Collection name is required",
                    }}
                  />
                  {/* For Postgres the db is in the connection string — allow loading
                      tables directly without choosing a database first. */}
                  {isPostgres && !collItems.length && (
                    <button
                      type="button"
                      onClick={() => loadCollections()}
                      disabled={loadingColls || !((connectionString || "").trim())}
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                    >
                      {loadingColls ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                      Load tables
                    </button>
                  )}
                  {collError && (
                    <p className="mt-1 text-xs text-red-500">{collError}</p>
                  )}
                </div>
              </div>
            )}

            {/* Large-dataset warnings */}
            {!manualEntry && (isLarge(selectedDb?.size_bytes) || isLarge(selectedColl?.size_bytes)) && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-800 space-y-0.5">
                  {isLarge(selectedColl?.size_bytes) ? (
                    <p>
                      The {collLabel.toLowerCase()}{" "}
                      <span className="font-semibold">{selectedColl?.name}</span> is{" "}
                      <span className="font-semibold">
                        {formatBytes(selectedColl?.size_bytes)}
                      </span>
                      {selectedColl?.count != null && (
                        <> ({formatCount(selectedColl.count)} rows)</>
                      )}
                      . Ingesting a dataset this large may take a while and use
                      significant resources.
                    </p>
                  ) : (
                    <p>
                      The database{" "}
                      <span className="font-semibold">{selectedDb?.name}</span> is{" "}
                      <span className="font-semibold">
                        {formatBytes(selectedDb?.size_bytes)}
                      </span>
                      . Pick a specific {collLabel.toLowerCase()} to narrow the
                      data you ingest.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Data Handling */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Data Handling
              </h3>
            </div>
            <p className="text-xs text-gray-500">
              Choose how rows from this {isPostgres ? "table" : "collection"} are
              ingested into the document store.
            </p>

            <CustomDropdownSelect
              name="ingest_mode"
              control={control as any}
              label="Ingest Mode"
              placeholder="Select ingest mode"
              options={[
                { value: "FILES", label: "Files — rows hold file URLs / base64" },
                { value: "DATA", label: "Data — convert rows into documents" },
              ]}
            />

            {isDataMode && (
              <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CustomDropdownSelect
                    name="data_granularity"
                    control={control as any}
                    label="Granularity"
                    placeholder="Select granularity"
                    options={[
                      { value: "PER_ITEM", label: "One document per row" },
                      { value: "WHOLE", label: "One document for all rows" },
                    ]}
                  />
                  <CustomDropdownSelect
                    name="data_format"
                    control={control as any}
                    label="Format"
                    placeholder="Select format"
                    options={[
                      { value: "JSON", label: "JSON" },
                      { value: "TXT", label: "Plain text (TXT)" },
                    ]}
                  />
                </div>

                <Input
                  name="filename_field"
                  control={control}
                  label="Filename Field (optional)"
                  placeholder="e.g., title — names each per-row document"
                />

                <Input
                  name="data_file_field"
                  control={control}
                  label="File Field (optional)"
                  placeholder="e.g., attachment_url — downloads a file referenced in the row"
                />
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isEdit ? "Updating..." : "Creating..."}</span>
                </>
              ) : (
                <span>
                  {isEdit ? "Update Configuration" : "Create Configuration"}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
